/**
 * FlightEntryForm Component
 * Form for creating and editing flight entries
 */

import { useState, FormEvent } from 'react';
import Input from '@/components/ui/Input';
import DatePicker from '@/components/ui/DatePicker';
import Button from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import type { FlightLog, CreateFlightLogRequest } from '@/types/flight';

interface FlightEntryFormProps {
  flight?: FlightLog; // If editing an existing flight log
  userId: number;
  onSubmit: (data: CreateFlightLogRequest) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
}

interface FormErrors {
  flightDate?: string;
  tailNumber?: string;
  logsheetNumber?: string;
  qNumber?: string;
  airTime?: string;
  flightTime?: string;
}

export default function FlightEntryForm({
  flight,
  userId,
  onSubmit,
  onCancel,
  isLoading = false,
}: FlightEntryFormProps) {
  const isEditing = !!flight;

  // Form state
  const [formData, setFormData] = useState<CreateFlightLogRequest>({
    userId,
    flightDate: flight?.flightDate.split('T')[0] || new Date().toISOString().split('T')[0],
    tailNumber: flight?.tailNumber || '',
    logsheetNumber: flight?.logsheetNumber || '',
    qNumber: flight?.qNumber || '',
    airTime: flight?.airTime || 0,
    flightTime: flight?.flightTime || 0,
    isShortNotice: flight?.isShortNotice || false,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Validate form
  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.flightDate) {
      newErrors.flightDate = 'Flight date is required';
    }

    if (!formData.tailNumber.trim()) {
      newErrors.tailNumber = 'Tail number is required';
    } else if (formData.tailNumber.length > 10) {
      newErrors.tailNumber = 'Tail number must be 10 characters or less';
    }

    if (!formData.logsheetNumber.trim()) {
      newErrors.logsheetNumber = 'Logsheet number is required';
    } else if (formData.logsheetNumber.length > 20) {
      newErrors.logsheetNumber = 'Logsheet number must be 20 characters or less';
    }

    if (formData.qNumber && formData.qNumber.length > 10) {
      newErrors.qNumber = 'Q number must be 10 characters or less';
    }

    if (formData.airTime <= 0) {
      newErrors.airTime = 'Air time must be greater than 0';
    } else if (formData.airTime > 24) {
      newErrors.airTime = 'Air time cannot exceed 24 hours';
    }

    if (formData.flightTime <= 0) {
      newErrors.flightTime = 'Flight time must be greater than 0';
    } else if (formData.flightTime > 24) {
      newErrors.flightTime = 'Flight time cannot exceed 24 hours';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  // Update form field
  const updateField = (field: keyof CreateFlightLogRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Flight Date */}
      <DatePicker
        label="Flight Date"
        value={formData.flightDate}
        onChange={(e) => updateField('flightDate', e.target.value)}
        error={errors.flightDate}
        required
        disabled={isLoading}
      />

      {/* Tail Number */}
      <Input
        label="Tail Number"
        value={formData.tailNumber}
        onChange={(e) => updateField('tailNumber', e.target.value.toUpperCase())}
        error={errors.tailNumber}
        placeholder="e.g., N12345"
        maxLength={10}
        required
        disabled={isLoading}
        showClear
      />

      {/* Logsheet Number */}
      <Input
        label="Logsheet Number"
        value={formData.logsheetNumber}
        onChange={(e) => updateField('logsheetNumber', e.target.value)}
        error={errors.logsheetNumber}
        placeholder="e.g., LS-2025-001"
        maxLength={20}
        required
        disabled={isLoading}
        showClear
      />

      {/* Q Number */}
      <Input
        label="Q Number"
        value={formData.qNumber}
        onChange={(e) => updateField('qNumber', e.target.value)}
        error={errors.qNumber}
        placeholder="e.g., Q12345 (optional)"
        maxLength={10}
        disabled={isLoading}
        showClear
      />

      {/* Time Fields - Side by Side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Air Time */}
        <Input
          label="Air Time (hours)"
          type="number"
          step="0.1"
          min="0"
          max="24"
          value={formData.airTime}
          onChange={(e) => updateField('airTime', parseFloat(e.target.value) || 0)}
          error={errors.airTime}
          placeholder="0.0"
          required
          disabled={isLoading}
        />

        {/* Flight Time */}
        <Input
          label="Flight Time (hours)"
          type="number"
          step="0.1"
          min="0"
          max="24"
          value={formData.flightTime}
          onChange={(e) => updateField('flightTime', parseFloat(e.target.value) || 0)}
          error={errors.flightTime}
          placeholder="0.0"
          required
          disabled={isLoading}
        />
      </div>

      {/* Short Notice Checkbox */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="isShortNotice"
          checked={formData.isShortNotice}
          onChange={(e) => updateField('isShortNotice', e.target.checked)}
          disabled={isLoading}
          className={cn(
            'w-5 h-5 rounded border-2 border-border',
            'text-primary focus:ring-2 focus:ring-primary/50',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'cursor-pointer transition-colors'
          )}
        />
        <label
          htmlFor="isShortNotice"
          className="text-sm font-medium text-foreground cursor-pointer select-none"
        >
          Short Notice Flight (affects pay calculation)
        </label>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          isLoading={isLoading}
        >
          {isEditing ? 'Update Flight' : 'Create Flight'}
        </Button>
      </div>
    </form>
  );
}
